import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosActividadesAPI, bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { ModalValidacion } from '../../components/bomberos/ModalValidacion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    CalendarDays,
    Plus,
    Search,
    ArrowLeft,
    Filter,
    Eye,
    Edit,
    Trash2,
    CheckCircle,
    XCircle,
    Clock,
    RefreshCw,
    FileCheck,
    MapPin,
    Users,
    Truck,
} from 'lucide-react';

export default function ActividadesExtraPage() {
    const navigate = useNavigate();
    const { tienePermiso, esAdmin, loading: loadingPermisos } = useBomberosPermisos();
    
    const [loading, setLoading] = useState(true);
    const [actividades, setActividades] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [filtroCompania, setFiltroCompania] = useState('todas');
    
    // Modal de validación
    const [showValidacion, setShowValidacion] = useState(false);
    const [actividadValidar, setActividadValidar] = useState(null);

    const puedeValidar = tienePermiso('validar_actividades') || esAdmin;
    const puedeCrear = tienePermiso('crear_actividades') || esAdmin;

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [actividadesRes, companiasRes] = await Promise.all([
                bomberosActividadesAPI.getAll(),
                bomberosCompaniaAPI.getAll(),
            ]);
            setActividades(actividadesRes.data);
            setCompanias(companiasRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar actividades');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar esta actividad?')) return;
        
        try {
            await bomberosActividadesAPI.delete(id);
            toast.success('Actividad eliminada');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const handleOpenValidacion = (actividad) => {
        setActividadValidar(actividad);
        setShowValidacion(true);
    };

    const getEstadoBadge = (estado) => {
        switch (estado) {
            case 'autorizado':
                return <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Autorizado</Badge>;
            case 'rechazado':
                return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rechazado</Badge>;
            default:
                return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pendiente</Badge>;
        }
    };

    // Filtrar actividades
    const filteredActividades = actividades.filter(act => {
        const matchSearch = act.actividad.toLowerCase().includes(searchTerm.toLowerCase()) ||
            act.lugar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            act.compania_nombre?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchEstado = filtroEstado === 'todos' || act.estado === filtroEstado;
        const matchCompania = filtroCompania === 'todas' || act.compania_id === filtroCompania;
        
        return matchSearch && matchEstado && matchCompania;
    });

    // Estadísticas
    const stats = {
        total: actividades.length,
        pendientes: actividades.filter(a => a.estado === 'pendiente').length,
        autorizadas: actividades.filter(a => a.estado === 'autorizado').length,
        rechazadas: actividades.filter(a => a.estado === 'rechazado').length,
    };

    if (loading || loadingPermisos) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-6 p-6 pt-20 md:pt-20">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid gap-4 md:grid-cols-4">
                        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <Skeleton className="h-64" />
                </div>
            </>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                <CalendarDays className="h-6 w-6 text-orange-500" />
                                Actividades Extra Programáticas
                            </h1>
                            <p className="text-muted-foreground">
                                Gestión de solicitudes de actividades
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Actualizar
                        </Button>
                        {puedeCrear && (
                            <Button onClick={() => navigate('/bomberos/actividades/nueva')} data-testid="new-actividad-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Nueva Actividad
                            </Button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid gap-4 md:grid-cols-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total</p>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                </div>
                                <CalendarDays className="h-8 w-8 text-muted-foreground" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Pendientes</p>
                                    <p className="text-2xl font-bold text-yellow-600">{stats.pendientes}</p>
                                </div>
                                <Clock className="h-8 w-8 text-yellow-500" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Autorizadas</p>
                                    <p className="text-2xl font-bold text-green-600">{stats.autorizadas}</p>
                                </div>
                                <CheckCircle className="h-8 w-8 text-green-500" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Rechazadas</p>
                                    <p className="text-2xl font-bold text-red-600">{stats.rechazadas}</p>
                                </div>
                                <XCircle className="h-8 w-8 text-red-500" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar por actividad, lugar..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9"
                                    data-testid="search-input"
                                />
                            </div>
                            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                                <SelectTrigger className="w-full md:w-48" data-testid="filter-estado">
                                    <Filter className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder="Estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="todos">Todos los estados</SelectItem>
                                    <SelectItem value="pendiente">Pendientes</SelectItem>
                                    <SelectItem value="autorizado">Autorizados</SelectItem>
                                    <SelectItem value="rechazado">Rechazados</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={filtroCompania} onValueChange={setFiltroCompania}>
                                <SelectTrigger className="w-full md:w-48" data-testid="filter-compania">
                                    <SelectValue placeholder="Compañía" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="todas">Todas las compañías</SelectItem>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.numero ? `${c.numero}ª ` : ''}{c.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Solicitudes de Actividades</CardTitle>
                        <CardDescription>
                            {filteredActividades.length} actividades encontradas
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {filteredActividades.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>No hay actividades registradas</p>
                                {puedeCrear && (
                                    <Button 
                                        variant="outline" 
                                        className="mt-4"
                                        onClick={() => navigate('/bomberos/actividades/nueva')}
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Crear primera actividad
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Fecha</TableHead>
                                        <TableHead>Actividad</TableHead>
                                        <TableHead>Lugar</TableHead>
                                        <TableHead>Compañía</TableHead>
                                        <TableHead>OBAC</TableHead>
                                        <TableHead>Asistentes</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredActividades.map((act) => (
                                        <TableRow key={act.id} data-testid={`actividad-${act.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                                                    {new Date(act.fecha).toLocaleDateString('es-CL')}
                                                </div>
                                                {act.hora_inicio && (
                                                    <div className="text-xs text-muted-foreground">
                                                        {act.hora_inicio} - {act.hora_fin || '...'}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium">{act.actividad}</div>
                                                {act.observaciones_solicitante && (
                                                    <div className="text-xs text-muted-foreground truncate max-w-48">
                                                        {act.observaciones_solicitante}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {act.lugar ? (
                                                    <div className="flex items-center gap-1">
                                                        <MapPin className="h-3 w-3 text-muted-foreground" />
                                                        <span className="truncate max-w-32">{act.lugar}</span>
                                                    </div>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>{act.compania_nombre || '-'}</TableCell>
                                            <TableCell>{act.obac_nombre || '-'}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Users className="h-3 w-3 text-muted-foreground" />
                                                    <span>{act.asistentes_ids?.length || 0}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{getEstadoBadge(act.estado)}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => navigate(`/bomberos/actividades/${act.id}`)}
                                                        title="Ver detalle"
                                                        data-testid={`ver-${act.id}`}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    {act.estado === 'pendiente' && (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => navigate(`/bomberos/actividades/${act.id}/editar`)}
                                                                title="Editar"
                                                                data-testid={`editar-${act.id}`}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            {puedeValidar && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleOpenValidacion(act)}
                                                                    title="Validar"
                                                                    className="text-primary"
                                                                    data-testid={`validar-${act.id}`}
                                                                >
                                                                    <FileCheck className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDelete(act.id)}
                                                                className="text-destructive"
                                                                title="Eliminar"
                                                                data-testid={`eliminar-${act.id}`}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Modal de Validación */}
            <ModalValidacion
                open={showValidacion}
                onOpenChange={setShowValidacion}
                item={actividadValidar}
                itemType="actividad"
                onValidar={(data) => bomberosActividadesAPI.validar(actividadValidar.id, data)}
                onSuccess={loadData}
            />
        </>
    );
}
